import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from '@/compat/react-router-dom';
import { getPartnerLoginPath, getPartnerPortalPathForRole } from '@/utils/partnerRoutes';

const PartnerPortalAuthContext = createContext(undefined);
const STORAGE_KEY = 'partner_portal_session_v1';

function readStoredSession() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.token || !parsed.user) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function usePartnerPortalAuth() {
  const context = useContext(PartnerPortalAuthContext);
  if (context === undefined) {
    throw new Error('usePartnerPortalAuth must be used within PartnerPortalAuthProvider');
  }
  return context;
}

export function PartnerPortalAuthProvider({ children }) {
  const navigate = useNavigate();
  const [session, setSession] = useState(readStoredSession);

  const saveSession = useCallback((nextSession) => {
    if (!nextSession?.token || !nextSession?.user) return;
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSession));
    setSession(nextSession);
  }, []);

  const clearSession = useCallback(({ redirect = true } = {}) => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    setSession(null);
    if (redirect) navigate(getPartnerLoginPath());
  }, [navigate]);

  const getPortalHomePath = useCallback(() => {
    const slug = session?.user?.partner_slug;
    const role = session?.user?.role || 'partner_seller';
    if (!slug) return getPartnerLoginPath();
    return getPartnerPortalPathForRole(slug, role);
  }, [session]);

  const value = useMemo(
    () => ({
      session,
      token: session?.token || '',
      user: session?.user || null,
      isAuthenticated: Boolean(session?.token),
      saveSession,
      clearSession,
      getPortalHomePath,
    }),
    [session, clearSession, saveSession, getPortalHomePath]
  );

  return (
    <PartnerPortalAuthContext.Provider value={value}>
      {children}
    </PartnerPortalAuthContext.Provider>
  );
}
